import React, { useState, useRef, useEffect } from "react";
import { getBotReply } from "../utils/chatbotEngine";
import { chatLookup, chatSend, chatMessages } from "../utils/api";

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "Hi! I'm Goldie, the Golden Gateway assistant. Ask me anything about our job endorsements, requirements, fees, or application process." }
  ]);
  const [input, setInput] = useState("");
  const bottomRef = useRef(null);

  // Live chat (saved conversation linked to an Endorsement ID)
  const [showLinkBox, setShowLinkBox] = useState(false);
  const [appIdInput, setAppIdInput] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linked, setLinked] = useState(null); // { applicantId, applicationId, name, address }
  const [liveMsgs, setLiveMsgs] = useState([]);

  // Show tooltip after 2s, hide after 6s, then pulse every 15s
  useEffect(() => {
    const show = setTimeout(() => setShowTooltip(true), 2000);
    const hide = setTimeout(() => setShowTooltip(false), 7000);
    const repeat = setInterval(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 5000);
    }, 18000);
    return () => { clearTimeout(show); clearTimeout(hide); clearInterval(repeat); };
  }, []);

  const handleOpen = () => {
    setOpen(!open);
    setShowTooltip(false);
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, liveMsgs, open]);

  // Poll the saved conversation once linked to an Endorsement ID
  useEffect(() => {
    if (!linked) return;
    let cancelled = false;
    const fetchMsgs = async () => {
      const res = await chatMessages(linked.applicantId);
      if (!cancelled && res.ok) setLiveMsgs(res.messages);
    };
    fetchMsgs();
    const interval = setInterval(fetchMsgs, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [linked]);

  const handleLink = async () => {
    const id = appIdInput.trim();
    if (!id) return;
    setLinking(true);
    setLinkError("");
    const res = await chatLookup(id);
    setLinking(false);
    if (res.ok) {
      setLinked({ applicantId: res.applicantId, applicationId: res.applicationId, name: res.name, address: res.address });
      setShowLinkBox(false);
    } else {
      setLinkError(res.error || "Endorsement ID not found.");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    if (linked) {
      setLiveMsgs((m) => [...m, { id: `tmp-${Date.now()}`, sender: "applicant", senderName: linked.name, text }]);
      await chatSend(linked.applicantId, linked.applicationId, linked.name, linked.address, text);
    } else {
      const reply = getBotReply(text);
      setMsgs((m) => [...m, { role: "user", text }, { role: "bot", text: reply }]);
    }
  };

  return (
    <>
      {/* Tooltip bubble */}
      {!open && showTooltip && (
        <div className="chatbot-tooltip" onClick={handleOpen}>
          <span>💬 Need help? Ask Goldie!</span>
          <button className="chatbot-tooltip-close" onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }}>×</button>
        </div>
      )}

      {/* Main button */}
      <button className={`chatbot-bubble ${!open ? "chatbot-pulse" : ""}`} onClick={handleOpen} aria-label="Open chatbot">
        <i className={`ti ${open ? "ti-x" : "ti-message-circle-2"}`}></i>
      </button>

      {open && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div>
              <div style={{ fontWeight: 600 }}>{linked ? "Live Chat" : "Goldie Assistant"}</div>
              <div style={{ fontSize: 11, color: "var(--gold-300)" }}>
                {linked ? `● Saved to ${linked.applicationId}` : "● Online"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {!linked && (
                <button onClick={() => setShowLinkBox((v) => !v)} title="Save this conversation" style={{ color: "var(--cream)" }}>
                  <i className="ti ti-link"></i>
                </button>
              )}
              <button onClick={() => setOpen(false)} style={{ color: "var(--cream)" }}>
                <i className="ti ti-x"></i>
              </button>
            </div>
          </div>

          {!linked && showLinkBox && (
            <div className="chatbot-link-box">
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                Enter your Endorsement ID to save this conversation. Your name and address will be filled in automatically, and our team can reply here.
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="mono"
                  placeholder="GGW-2026-XXXXXX"
                  value={appIdInput}
                  onChange={(e) => setAppIdInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLink()}
                />
                <button onClick={handleLink} disabled={linking}>{linking ? "..." : "Link"}</button>
              </div>
              {linkError && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 4 }}>{linkError}</div>}
            </div>
          )}

          {!linked && !showLinkBox && (
            <div className="chatbot-save-hint">
              Without an Endorsement ID, this conversation can't be saved.{" "}
              <button onClick={() => setShowLinkBox(true)}>Add Endorsement ID</button>
            </div>
          )}

          <div className="chatbot-messages">
            {linked ? (
              <>
                <div className="chat-msg bot" style={{ whiteSpace: "pre-line" }}>
                  {`You're connected as ${linked.name}${linked.address ? ` (${linked.address})` : ""}. This chat is saved to application ${linked.applicationId} and our team will reply here.`}
                </div>
                {liveMsgs.map((m) => (
                  <div key={m.id} className={`chat-msg ${m.sender === "applicant" ? "user" : "bot"}`} style={{ whiteSpace: "pre-line" }}>
                    {m.sender !== "applicant" && (
                      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>{m.senderName || (m.sender === "bot" ? "Goldie" : "Admin")}</div>
                    )}
                    {m.text}
                  </div>
                ))}
              </>
            ) : (
              msgs.map((m, i) => (
                <div key={i} className={`chat-msg ${m.role}`} style={{ whiteSpace: "pre-line" }}>{m.text}</div>
              ))
            )}
            <div ref={bottomRef} />
          </div>
          <div className="chatbot-input-row">
            <input
              type="text" placeholder={linked ? "Message our team..." : "Ask a question..."}
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <button onClick={send}><i className="ti ti-send"></i></button>
          </div>
        </div>
      )}
    </>
  );
}
